/* Size guide drawer (DESIGN.md §9 row 56).
   The native <dialog> does the focus trap and Escape, and the Inches / CM
   toggle is CSS off the checked radio. This adds the backdrop click, a short
   slide-out before close, the page scroll lock and the focus return. The
   element returns focus on its own, but only to whatever held it at open, and
   a mouse click does not focus a button in every browser, so the trigger is
   refocused whenever the close leaves focus on the body.

   The lock is the theme's own ref-counted one (html[scroll-lock]) rather than
   an inline overflow, so it stacks with the cart drawer and the filter drawer
   and so bettino-header-scroll.js still sees a dialog is open. */

import { lockScroll, unlockScroll } from '@theme/utilities';

const CLOSE_MS = 200;

/** Set once the shopper changes the variant picker, so the pre-checked size is not taken for a choice. */
let sizePicked = false;

class BettinoSizeGuide extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-dialog]');
    this.trigger = this.querySelector('[data-trigger]');
    if (!this.dialog || !this.trigger) return;

    // cancel and close do not bubble, so they are the only two that have to be
    // bound on the dialog itself; the clicks are delegated from the document.
    // A variant change re-renders the details column, so the guard is on the
    // dialog node rather than on the element: a re-render hands this element a
    // new dialog to bind, while a plain reconnection hands back the same one.
    if (this.boundDialog === this.dialog) return;
    this.boundDialog = this.dialog;

    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });

    this.dialog.addEventListener('close', () => this.unlock());
  }

  disconnectedCallback() {
    unlockScroll(this);
  }

  open() {
    if (this.dialog.open) return;
    this.dialog.removeAttribute('data-closing');
    this.markSelectedSize();
    lockScroll(this);
    this.dialog.showModal();
  }

  // Row 86: the column of the size picked on the page gets the grey highlight.
  // Read-only: it looks at the variant picker's checked values and matches
  // them against the table's size columns, whatever the option is called.
  // Shopify pre-checks the first available size on load, which the shopper
  // never chose, so nothing is marked until they pick one: a change on the
  // picker this visit, or a ?variant= in the URL (the picker writes it on a
  // pick, and a shared link carries it).
  markSelectedSize() {
    const chosen = sizePicked || new URLSearchParams(window.location.search).has('variant');
    const scope = this.closest('.shopify-section') || this.ownerDocument;
    const picked = new Set(
      chosen
        ? Array.from(
            scope.querySelectorAll('variant-picker input[type="radio"]:checked, variant-picker select'),
            (input) => String(input.value).trim().toUpperCase()
          )
        : []
    );

    this.dialog.querySelectorAll('[data-size]').forEach((cell) => {
      const size = cell.getAttribute('data-size') || '';
      cell.toggleAttribute('data-selected', picked.has(size.toUpperCase()));
    });
  }

  close() {
    if (!this.dialog.open || this.dialog.hasAttribute('data-closing')) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.dialog.setAttribute('data-closing', '');
    setTimeout(() => this.dialog.close(), reduced ? 0 : CLOSE_MS);
  }

  unlock() {
    this.dialog.removeAttribute('data-closing');
    unlockScroll(this);

    // Focus return. The element restores focus itself, but only to whatever
    // held it at open, and a mouse click does not focus a button in every
    // browser; a close with nothing to restore leaves focus on the body or on
    // the close button inside the now-hidden panel. Either way it is stranded,
    // so the trigger takes it back. Focus the shopper moved elsewhere is left
    // alone. The check is queued behind the element's own restore, on a timer
    // rather than a frame, because a tab that is not painting never runs the
    // frame callback.
    setTimeout(() => {
      const focused = this.ownerDocument.activeElement;
      const stranded = !focused || focused === this.ownerDocument.body || this.dialog.contains(focused);
      if (stranded) this.trigger.focus();
    }, 0);
  }
}

if (!customElements.get('bettino-size-guide')) {
  customElements.define('bettino-size-guide', BettinoSizeGuide);

  // Delegated for the same reason as the clicks below: the picker re-renders.
  document.addEventListener('change', (event) => {
    if (event.target instanceof Element && event.target.closest('variant-picker')) sizePicked = true;
  });

  // One delegated listener rather than one per element. The variant picker
  // re-renders the details column on a size or colour change, and a trigger
  // that came back as a new node would have lost a listener bound at load.
  document.addEventListener('click', (event) => {
    const { target } = event;
    if (!(target instanceof Element)) return;

    // A text link to #size-guide (the PDP Size & fit row) opens the drawer.
    // Without this script the same link falls through to the Size Guide page.
    const link = target.closest('a[href$="#size-guide"]');
    const drawer = document.querySelector('bettino-size-guide');
    if (link && drawer instanceof BettinoSizeGuide && drawer.dialog) {
      event.preventDefault();
      drawer.open();
      return;
    }

    const host = target.closest('bettino-size-guide');
    if (!(host instanceof BettinoSizeGuide) || !host.dialog) return;

    if (target.closest('[data-trigger]')) {
      host.open();
    } else if (target.closest('[data-close]')) {
      host.close();
    } else if (target === host.dialog) {
      // Only the dialog box itself is hit outside the panel's own children
      // when the backdrop is clicked.
      host.close();
    }
  });
}
